import { create } from "zustand";
import {
  Node,
  Edge,
  Connection,
  MarkerType,
  NodeChange,
  EdgeChange,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
} from "@xyflow/react";

interface FlowStore {
  nodes: Node[];
  edges: Edge[];
  nodeIDs: Record<string, number>;
  getNodeID: (nodeType: string) => string;
  addNode: (node: Node) => void;
  removeNode: (nodeId: string) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  updateNodeField: (
    nodeId: string,
    fieldName: string,
    fieldValue: unknown,
  ) => void;
  removeEdge: (edgeId: string) => void;
}

const useFlowStore = create<FlowStore>((set, get) => ({
  nodes: [],
  edges: [],
  nodeIDs: {},

  getNodeID: (nodeType: string) => {
    const { nodeIDs } = get();
    const id = nodeIDs[nodeType];
    if (id === undefined) {
      nodeIDs[nodeType] = 0;
    }
    nodeIDs[nodeType] += 1;
    return `${nodeType}_${nodeIDs[nodeType]}`;
  },

  addNode: (node: Node) => {
    set((state) => ({ nodes: [...state.nodes, node] }));
  },

  removeNode: (nodeId: string) => {
    set((state) => ({
      nodes: state.nodes.filter((node) => node.id !== nodeId),
      edges: state.edges.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId,
      ),
    }));
  },

  onNodesChange: (changes: NodeChange[]) => {
    set((state) => ({
      nodes: applyNodeChanges(changes, state.nodes),
    }));
  },

  onEdgesChange: (changes: EdgeChange[]) => {
    set((state) => ({
      edges: applyEdgeChanges(changes, state.edges),
    }));
  },

  onConnect: (connection: Connection) => {
    set((state) => ({
      edges: addEdge(
        {
          ...connection,
          animated: true,
          markerEnd: {
            type: MarkerType.Arrow,
            height: 20,
            width: 20,
          },
          type: "buttonEdge",
        },
        state.edges,
      ),
    }));
  },

  updateNodeField: (nodeId: string, fieldName: string, fieldValue: unknown) => {
    set((state) => ({
      nodes: state.nodes.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: { ...node.data, [fieldName]: fieldValue },
          };
        }
        return node;
      }),
    }));
  },

  removeEdge: (edgeId: string) => {
    set((state) => ({
      edges: state.edges.filter((edge) => edge.id !== edgeId),
    }));
  },
}));

// This maintains the same API as before
export const useFlow = () => {
  const store = useFlowStore();
  return store;
};

// For debugging purposes
// useFlowStore.subscribe((state) => {
//     console.log("nodes:", state.nodes, "edges:", state.edges);
// });
